import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUndo, faSync } from '@fortawesome/free-solid-svg-icons';
import BiblioService from '../../services/BiblioService';
import { Loading } from '../Loading';
import { formatMoney } from './constants';
import Autocomplete from '../Autocomplete';

function todayISO() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

const EMPTY_FILTERS = { prefix: '', init: todayISO(), end: todayISO(), currency: '' };
const CURRENCY_OPTIONS = [
    { label: 'MXN', value: 'MXN' },
    { label: 'USD', value: 'USD' },
    { label: 'EUR', value: 'EUR' },
];

export function BiblioReport() {
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [drafts, setDrafts] = useState(EMPTY_FILTERS);
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchReport = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await BiblioService.getReport(filters);
            setStores(res.stores || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { fetchReport(); }, [fetchReport]);

    const set = (name, value) => setDrafts((prev) => ({ ...prev, [name]: value }));
    const handleSearch = () => setFilters({ ...drafts, prefix: drafts.prefix.toUpperCase() });
    const handleReset = () => { setDrafts(EMPTY_FILTERS); setFilters(EMPTY_FILTERS); };

    return (
        <>
            <div className="filters-panel mb-3">
                <div className="row g-3 align-items-end">
                    <div className="col">
                        <label className="fw-bold mb-1 d-block text-white-50" style={{ fontSize: '0.85rem' }}>Prefix</label>
                        <input
                            type="text"
                            className="form-control bg-dark text-white border-secondary py-2"
                            value={drafts.prefix}
                            placeholder="Ej. TST"
                            onChange={(e) => set('prefix', e.target.value)}
                        />
                    </div>
                    <div className="col">
                        <label className="fw-bold mb-1 d-block text-white-50" style={{ fontSize: '0.85rem' }}>Desde</label>
                        <input
                            type="date"
                            className="form-control bg-dark text-white border-secondary py-2"
                            value={drafts.init}
                            onChange={(e) => set('init', e.target.value)}
                        />
                    </div>
                    <div className="col">
                        <label className="fw-bold mb-1 d-block text-white-50" style={{ fontSize: '0.85rem' }}>Hasta</label>
                        <input
                            type="date"
                            className="form-control bg-dark text-white border-secondary py-2"
                            value={drafts.end}
                            onChange={(e) => set('end', e.target.value)}
                        />
                    </div>
                    <div className="col">
                        <label className="fw-bold mb-1 d-block text-white-50" style={{ fontSize: '0.85rem' }}>Moneda</label>
                        <Autocomplete
                            options={CURRENCY_OPTIONS}
                            value={drafts.currency ? { label: drafts.currency, value: drafts.currency } : null}
                            onChange={(opt) => set('currency', opt ? opt.value : '')}
                            isDark
                            isClearable
                            placeholder="MXN/USD/EUR"
                        />
                    </div>
                    <div className="col-auto d-flex gap-2">
                        <button className="btn btn-ink-search" onClick={handleSearch}>
                            <FontAwesomeIcon icon={faSearch} className="me-1" /> Buscar
                        </button>
                        <button className="btn btn-outline-secondary border-secondary text-white-50 px-3" onClick={handleReset}>
                            <FontAwesomeIcon icon={faUndo} className="me-1" /> Limpiar
                        </button>
                        <button className="btn btn-ink-icon" title="Recargar" onClick={fetchReport}>
                            <FontAwesomeIcon icon={faSync} />
                        </button>
                    </div>
                </div>
            </div>

            {error && <div className="alert alert-danger py-2">{error}</div>}

            {loading ? (
                <Loading />
            ) : (
                <div className="modern-table-container">
                    <table className="modern-table striped-table">
                        <thead>
                            <tr>
                                <th>Cuenta</th>
                                <th>Títulos</th>
                                <th>Usuarios</th>
                                <th>Ventas</th>
                                <th>Tienda</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stores.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-center text-white-50 py-4">
                                        No hay tiendas para los filtros aplicados
                                    </td>
                                </tr>
                            )}
                            {stores.map((store) => {
                                const actual = store.actual || {};
                                return (
                                    <tr key={store.prefix}>
                                        <td className="short fw-bold text-white">
                                            <span className="badge-status me-2">{store.prefix}</span>
                                            {store.account}
                                        </td>
                                        <td className="short text-white-50">
                                            {actual.titles === null || actual.titles === undefined ? '—' : actual.titles}
                                            {store.titles_limit ? <span className="text-white-50"> / {store.titles_limit}</span> : null}
                                        </td>
                                        <td className="short text-white-50">
                                            {actual.readers === null || actual.readers === undefined ? '—' : actual.readers}
                                            {store.users_limit ? <span className="text-white-50"> / {store.users_limit}</span> : null}
                                        </td>
                                        <td className="short text-white-50">
                                            {Array.isArray(actual.sales) && actual.sales.length > 0 ? (
                                                actual.sales.map((s) => (
                                                    <div key={s.currency}>{formatMoney(s.total, s.currency)} <span className="opacity-50">({s.count})</span></div>
                                                ))
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                        <td className="short">
                                            <span className={`badge-status ${actual.reachable ? 'status-on' : 'status-off'}`}>
                                                {actual.reachable ? 'En línea' : 'Sin acceso'}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}

export default BiblioReport;
